/**
 * Model vs Actual — Science.co
 *
 * The growth model from "Science.co Model 2026-2027_Conservative.xlsx", run
 * live in the browser against what actually happened.
 *
 *   Plan         the model on the assumptions below.
 *   Actual       what happened, month by month (the month in progress to date).
 *   Re-forecast  the same model, with every month that happened replaced by
 *                what happened and the rest projected from there.
 *
 * Assumptions start from config/scienceModel.js and are editable here, along
 * with four levers that scale whole curves at once. Edits last for the browser
 * session and are never saved anywhere else.
 *
 * Science.co only, on purpose — see config/scienceModel.js.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { Loader2, RefreshCw, RotateCcw } from 'lucide-react'

import { scienceModelAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Badge,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui'
import ModelChart, { SERIES_COLORS } from '../components/scienceModel/ModelChart'
import AssumptionsEditor from '../components/scienceModel/AssumptionsEditor'
import PnlStatement from '../components/scienceModel/PnlStatement'
import DailyView from '../components/scienceModel/DailyView'
import WeeklyView from '../components/scienceModel/WeeklyView'
import { Tile, Notice } from '../components/scienceModel/parts'
import MonthProjection from '../components/scienceModel/MonthProjection'
import {
  SCIENCE_MODEL_DEFAULTS, MODEL_MONTHS, PLANS, PLAN_LABELS,
} from '../config/scienceModel'
import { runModel, retentionComparison, sumField } from '../lib/scienceModel'
import { dailyPlan, rampFactor, rampedAssumptions, addDays } from '../lib/scienceModelDaily'
import {
  setIn, applyLevers, NEUTRAL_LEVERS, threeWaySeries, variance, monthLabel,
  FORMATTERS, formatVariance, formatPercent, withLivePrices,
} from '../lib/scienceModelView'

const SCIENCE_CLINIC_ID = 'scienceco'
const STORAGE_KEY = 'scienceModel:v1'

const LEVERS = [
  { key: 'spend', label: 'Ad spend', hint: 'scales month-one spend, and so every month after it' },
  { key: 'cac', label: 'CAC', hint: 'higher is worse: fewer customers for the same spend' },
  { key: 'price', label: 'Prices', hint: 'every plan, molecule and price book' },
  { key: 'retention', label: 'Renewals', hint: 'every renewal rate, capped at 100%' },
]

/** Session state, read defensively: storage can be absent or full of junk. */
function loadSession() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null')
    if (saved?.assumptions?.acquisition && saved?.levers) return saved
  } catch { /* fall through to defaults */ }
  return null
}

function saveSession(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch { /* a page that cannot remember is still a working page */ }
}

/** Months down, Plan / Actual / Re-forecast / vs plan across, per metric. */
function MetricTable({ metrics, plan, recorded, reforecast, ramped = null }) {
  const columns = ramped ? ['Plan', 'Ramped', 'Actual', 'Re-fcst', 'vs plan'] : ['Plan', 'Actual', 'Re-fcst', 'vs plan']
  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs tabular-nums">
          <thead>
            <tr className="border-b">
              <th rowSpan={2} className="sticky left-0 bg-card px-3 py-2 text-left font-medium text-muted-foreground">Month</th>
              {metrics.map(metric => (
                <th key={metric.label} colSpan={columns.length} className="px-2 pt-2 text-center font-semibold border-l">{metric.label}</th>
              ))}
            </tr>
            <tr className="border-b">
              {metrics.map(metric => columns.map((h, i) => (
                <th key={`${metric.label}-${h}`} className={`px-2 pb-2 text-right font-medium text-muted-foreground whitespace-nowrap ${i === 0 ? 'border-l' : ''}`}>{h}</th>
              )))}
            </tr>
          </thead>
          <tbody>
            {plan.map((planRow, i) => (
              <tr key={planRow.month} className="border-b last:border-0">
                <td className="sticky left-0 bg-card px-3 py-1.5 whitespace-nowrap">
                  {monthLabel(planRow.month)}
                  {recorded[i]?.source === 'paced' && <span className="ml-1 text-muted-foreground">(to date)</span>}
                </td>
                {metrics.map(metric => {
                  const full = FORMATTERS[metric.format].full
                  const happened = recorded[i]?.source !== 'forecast'
                  const planValue = planRow[metric.field]
                  const forecastValue = reforecast[i][metric.field]
                  return [
                    <td key="p" className="px-2 py-1.5 text-right border-l">{full(planValue)}</td>,
                    ramped && <td key="rp" className="px-2 py-1.5 text-right">{full(ramped[i][metric.field])}</td>,
                    <td key="a" className="px-2 py-1.5 text-right">{happened ? full(recorded[i][metric.field]) : ''}</td>,
                    <td key="r" className={`px-2 py-1.5 text-right ${happened ? '' : 'text-muted-foreground'}`}>{full(forecastValue)}</td>,
                    <td key="v" className="px-2 py-1.5 text-right">
                      {metric.format === 'percent' ? '' : formatVariance(variance(forecastValue, planValue))}
                    </td>,
                  ]
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--popover))',
  color: 'hsl(var(--popover-foreground))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
  fontSize: 12,
}
const AXIS_TICK = { fill: 'hsl(var(--muted-foreground))', fontSize: 11 }

export default function ScienceModelPage() {
  const { currentClinicId } = useAuthStore()
  const isScience = currentClinicId === SCIENCE_CLINIC_ID

  const [session] = useState(loadSession)
  const [assumptions, setAssumptions] = useState(session?.assumptions || SCIENCE_MODEL_DEFAULTS)
  const [levers, setLevers] = useState(session?.levers || NEUTRAL_LEVERS)
  const [spendMode, setSpendMode] = useState(session?.spendMode || 'plan')
  const [view, setView] = useState('monthly')
  const [tab, setTab] = useState('acquisition')
  const [retentionPlan, setRetentionPlan] = useState('monthly')

  const [actuals, setActuals] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => { saveSession({ assumptions, levers, spendMode }) }, [assumptions, levers, spendMode])

  // Manifold's live price list. The model prices on it wherever it has a
  // price, and on the sheet wherever it does not.
  const [livePrices, setLivePrices] = useState(null)
  useEffect(() => {
    if (!isScience) return
    scienceModelAPI.getPrices()
      .then(response => setLivePrices(response.data.data))
      .catch(err => setLivePrices({ checked: false, reason: err.response?.data?.error?.message || err.message }))
  }, [isScience])

  // What "unedited" means: the sheet, with live prices laid over it.
  const baseline = useMemo(
    () => withLivePrices(SCIENCE_MODEL_DEFAULTS, livePrices?.checked ? livePrices.prices : null),
    [livePrices]
  )

  // Adopt live prices when they arrive — unless prices were already edited by
  // hand this session, which is a choice the page should not overwrite.
  useEffect(() => {
    if (!livePrices?.checked) return
    setAssumptions(prev => (
      JSON.stringify(prev.prices) === JSON.stringify(SCIENCE_MODEL_DEFAULTS.prices)
        ? { ...prev, prices: baseline.assumptions.prices }
        : prev
    ))
  }, [livePrices, baseline])

  const edited = JSON.stringify(assumptions) !== JSON.stringify(baseline.assumptions)
  const levered = LEVERS.some(l => levers[l.key] !== 1)

  const onEdit = useCallback((path, value) => setAssumptions(prev => setIn(prev, path, value)), [])

  const reset = () => {
    setAssumptions(baseline.assumptions)
    setLevers(NEUTRAL_LEVERS)
    setSpendMode('plan')
  }

  const fetchActuals = useCallback(async () => {
    if (!isScience) return
    try {
      setLoading(true)
      setError(null)
      const response = await scienceModelAPI.getActuals({ startMonth: assumptions.startMonth, months: MODEL_MONTHS })
      if (response.data.success) setActuals(response.data.data)
    } catch (err) {
      console.error('[SCIENCE-MODEL] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
      setActuals(null)
    } finally {
      setLoading(false)
    }
  }, [isScience, assumptions.startMonth])

  useEffect(() => { fetchActuals() }, [fetchActuals])

  // Actuals for a different start month are stale the moment it changes.
  const usable = actuals?.startMonth === assumptions.startMonth ? actuals : null

  const effective = useMemo(() => applyLevers(assumptions, levers), [assumptions, levers])
  const plan = useMemo(() => runModel(effective), [effective])
  const recorded = useMemo(() => (usable ? runModel(effective, { actuals: usable, pace: false }) : plan.map(r => ({ ...r, source: 'forecast' }))), [effective, usable, plan])

  // ── Ramped plan ────────────────────────────────────────────────────
  // The model at our own spend level: the same growth rates, scaled so its
  // last seven full days of spend match what we actually spent.
  const [trailing, setTrailing] = useState(null)
  const fetchTrailing = useCallback(async () => {
    if (!isScience) return
    const todayEt = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date())
    try {
      const response = await scienceModelAPI.getDayActuals({ startDate: addDays(todayEt, -7), endDate: addDays(todayEt, -1) })
      if (response.data.success) setTrailing(response.data.data)
    } catch (err) {
      console.error('[SCIENCE-MODEL] Trailing spend error:', err)
      setTrailing(null)
    }
  }, [isScience])
  useEffect(() => { fetchTrailing() }, [fetchTrailing])

  const daily = useMemo(() => dailyPlan(effective, plan), [effective, plan])
  const ramp = useMemo(
    () => (trailing?.ad_spend_available ? rampFactor(daily, trailing.days, trailing.asOf) : null),
    [daily, trailing]
  )
  const ramped = useMemo(() => {
    if (!ramp) return null
    const a = rampedAssumptions(effective, ramp.factor)
    const rows = runModel(a)
    return { assumptions: a, plan: rows, daily: dailyPlan(a, rows) }
  }, [effective, ramp])

  // "Follow ramped plan" is the ramped assumptions following their own budget.
  // Actual months take actual spend either way, so only the months after differ.
  const reforecast = useMemo(() => {
    if (!usable) return plan
    if (spendMode === 'ramped') return runModel(ramped ? ramped.assumptions : effective, { actuals: usable, spendMode: 'plan' })
    return runModel(effective, { actuals: usable, spendMode })
  }, [effective, usable, spendMode, plan, ramped])

  const series = useCallback(
    (field) => threeWaySeries(plan, recorded, usable ? reforecast : null, field, ramped?.plan),
    [plan, recorded, reforecast, usable, ramped]
  )

  // The newest month that has happened, or month one before anything has.
  const latestIndex = Math.max(0, recorded.map(r => r.source).lastIndexOf('paced'), recorded.map(r => r.source).lastIndexOf('actual'))
  const latest = { plan: plan[latestIndex], recorded: recorded[latestIndex], reforecast: reforecast[latestIndex] }
  const latestHappened = latest.recorded.source !== 'forecast'
  const latestPaced = latest.recorded.source === 'paced'

  const mix = useMemo(() => {
    if (!usable?.cohorts_checked) return null
    const totals = Object.fromEntries(PLANS.map(p => [p, 0]))
    for (const month of Object.values(usable.months)) {
      for (const p of PLANS) totals[p] += month.new_by_plan?.[p] || 0
    }
    const all = PLANS.reduce((s, p) => s + totals[p], 0)
    return PLANS.map(p => ({
      label: PLAN_LABELS[p],
      model: effective.planSplit[p],
      actual: all > 0 ? totals[p] / all : null,
      customers: totals[p],
    }))
  }, [usable, effective])

  const retention = useMemo(
    () => retentionComparison(effective, usable, retentionPlan).filter(point => point.age > 0),
    [effective, usable, retentionPlan]
  )

  if (!isScience) {
    return (
      <div className="p-4 sm:p-6">
        <Notice>Model vs Actual is built on Science.co&apos;s growth model. Select Science.co to view it.</Notice>
      </div>
    )
  }

  const tileNote = latestPaced
    ? `${monthLabel(latest.plan.month)} · day ${Math.ceil(usable.months[latest.plan.month].days_elapsed)} of ${usable.months[latest.plan.month].days_in_month}, paced to month end on the plan's daily ramp`
    : latestHappened ? monthLabel(latest.plan.month) : `Plan begins ${monthLabel(latest.plan.month)}`
  const tileValue = (field) => (latestHappened ? latest.reforecast[field] : null)

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold">Model vs Actual</h1>
          <p className="text-xs text-muted-foreground">
            Science.co growth model (Conservative, 2026–27) against what actually happened
            {usable?.asOf && ` · actuals to ${usable.asOf}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(edited || levered) && <Badge variant="secondary">Scenario edited</Badge>}
          <Button variant="outline" size="sm" onClick={reset} disabled={!edited && !levered && spendMode === 'plan'}>
            <RotateCcw className="h-4 w-4" />
            <span className="ml-1.5">Reset assumptions</span>
          </Button>
          <Button variant="outline" size="sm" onClick={fetchActuals} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">Refresh</span>
          </Button>
        </div>
      </div>

      {/* This month's close, from actuals */}
      <MonthProjection
        recent={trailing?.days}
        planRow={plan.find(row => row.month === new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date()).slice(0, 7)) || null}
      />

      {/* Controls */}
      <Card>
        <CardContent className="p-4 grid gap-4 lg:grid-cols-[auto_auto_1fr] lg:items-end">
          <label className="space-y-1">
            <span className="block text-xs text-muted-foreground">Model month one</span>
            <input
              type="month"
              value={assumptions.startMonth}
              onChange={e => e.target.value && onEdit(['startMonth'], e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            />
          </label>
          <div className="space-y-1">
            <span className="block text-xs text-muted-foreground">Re-forecast spend after actuals</span>
            <div className="flex rounded-lg border border-input bg-background p-1">
              {[
                { value: 'plan', label: 'Follow plan budget' },
                { value: 'ramped', label: 'Follow ramped plan', disabled: !ramped },
                { value: 'actuals', label: 'Grow from actual spend' },
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setSpendMode(option.value)}
                  disabled={option.disabled}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap disabled:opacity-40 ${
                    spendMode === option.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {LEVERS.map(lever => (
              <label key={lever.key} className="space-y-1" title={lever.hint}>
                <span className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{lever.label}</span>
                  <span className="tabular-nums font-medium">{formatPercent(levers[lever.key], 0)}</span>
                </span>
                <input
                  type="range" min={0.5} max={1.5} step={0.05}
                  value={levers[lever.key]}
                  onChange={e => setLevers(prev => ({ ...prev, [lever.key]: Number(e.target.value) }))}
                  className="w-full accent-primary"
                  aria-label={`${lever.label} lever`}
                />
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Caveats that change how the numbers read */}
      {error && <Notice tone="error">Actuals could not be loaded: {error}. The plan is shown on its own.</Notice>}
      {usable && !usable.cohorts_checked && (
        <Notice>
          Plan lengths and renewals could not be read from Manifold ({usable.cohorts_reason}). Actual customers are
          split on the assumed plan mix, and actual retention is unavailable.
        </Notice>
      )}
      {usable && !usable.refunds_checked && usable.cohorts_checked && (
        <Notice>Refunds could not be read from Manifold ({usable.refunds_reason}); actual months use the assumed refund rate.</Notice>
      )}
      {usable && usable.ad_spend_available === false && (
        <Notice>No ad account has reported spend for Science.co, so actual spend and CAC read as zero.</Notice>
      )}
      <div className="flex rounded-lg border border-input bg-background p-1 w-fit">
        {[
          { value: 'monthly', label: 'Monthly' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'daily', label: 'Daily' },
        ].map(option => (
          <button
            key={option.value}
            onClick={() => setView(option.value)}
            className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
              view === option.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {view === 'daily' ? (
        <DailyView plan={plan} daily={daily} ramped={ramped} />
      ) : view === 'weekly' ? (
        <WeeklyView startMonth={assumptions.startMonth} daily={daily} ramped={ramped} />
      ) : (
      <>
      {/* Headline */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {['newCustomers', 'revenue', 'spend'].map(field => (
          <Tile
            key={field}
            title={{ newCustomers: 'New customers', revenue: 'Revenue', spend: 'Ad spend' }[field]}
            format={field === 'newCustomers' ? 'count' : 'money'}
            value={tileValue(field)} plan={latest.plan[field]}
            delta={latestHappened ? variance(latest.reforecast[field], latest.plan[field]) : null}
            ramped={ramped ? ramped.plan[latestIndex][field] : null}
            rampedDelta={ramped && latestHappened ? variance(latest.reforecast[field], ramped.plan[latestIndex][field]) : null}
            note={tileNote}
          />
        ))}
        <Tile title="CAC" value={latestHappened ? latest.recorded.cac : null} plan={latest.plan.cac} invert
          delta={latestHappened ? variance(latest.recorded.cac, latest.plan.cac) : null}
          note={latestHappened ? `${monthLabel(latest.plan.month)}${latestPaced ? ' to date' : ''}` : tileNote} />
        <Tile title={`Revenue, ${MODEL_MONTHS} months`} value={sumField(reforecast, 'revenue')} plan={sumField(plan, 'revenue')}
          delta={usable ? variance(sumField(reforecast, 'revenue'), sumField(plan, 'revenue')) : null}
          ramped={ramped ? sumField(ramped.plan, 'revenue') : null}
          rampedDelta={ramped && usable ? variance(sumField(reforecast, 'revenue'), sumField(ramped.plan, 'revenue')) : null}
          note="Re-forecast vs plan" />
        <Tile title={`Net operating income, ${MODEL_MONTHS} months`} value={sumField(reforecast, 'netOperatingIncome')} plan={sumField(plan, 'netOperatingIncome')}
          delta={usable ? variance(sumField(reforecast, 'netOperatingIncome'), sumField(plan, 'netOperatingIncome')) : null}
          ramped={ramped ? sumField(ramped.plan, 'netOperatingIncome') : null}
          rampedDelta={ramped && usable ? variance(sumField(reforecast, 'netOperatingIncome'), sumField(ramped.plan, 'netOperatingIncome')) : null}
          note="Re-forecast vs plan" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="acquisition">Acquisition</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="retention">Retention</TabsTrigger>
          <TabsTrigger value="pnl">P&amp;L</TabsTrigger>
          <TabsTrigger value="assumptions">Assumptions</TabsTrigger>
        </TabsList>

        <TabsContent value="acquisition" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <ModelChart title="Ad spend" data={series('spend')} />
            <ModelChart title="New customers" format="count" data={series('newCustomers')} />
            <ModelChart title="CAC" description="Ad spend ÷ new customers" data={series('cac')} />
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Plan-length mix</CardTitle>
                <CardDescription className="text-xs">
                  Share of new customers on each plan, assumed vs bought since month one.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {mix ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={mix} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                      <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                      <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={v => formatPercent(v, 0)} width={44} />
                      <Tooltip
                        contentStyle={TOOLTIP_STYLE}
                        cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                        formatter={(value, name, item) => [
                          name === 'Actual' ? `${formatPercent(value)} (${item.payload.customers} customers)` : formatPercent(value),
                          name,
                        ]}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} iconSize={10} />
                      <Bar dataKey="model" name="Plan" fill={SERIES_COLORS.plan} radius={[4, 4, 0, 0]} maxBarSize={32} />
                      <Bar dataKey="actual" name="Actual" fill={SERIES_COLORS.actual} radius={[4, 4, 0, 0]} maxBarSize={32} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-xs text-muted-foreground py-8 text-center">
                    {usable ? 'Plan lengths are unavailable — see the note above.' : 'Waiting for actuals.'}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
          <MetricTable
            plan={plan} recorded={recorded} reforecast={reforecast} ramped={ramped?.plan}
            metrics={[
              { label: 'Ad spend', field: 'spend', format: 'money' },
              { label: 'New customers', field: 'newCustomers', format: 'count' },
              { label: 'CAC', field: 'cac', format: 'money' },
            ]}
          />
        </TabsContent>

        <TabsContent value="revenue" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <ModelChart title="Revenue" data={series('revenue')} />
            <ModelChart title="New-customer revenue" data={series('newRevenue')} />
            <ModelChart title="Recurring revenue" description="Renewals, plus any charge the Daily Report could not call new or renewal" data={series('recurringRevenue')} />
            <ModelChart title="New-customer AOV" data={series('newAov')} />
          </div>
          <MetricTable
            plan={plan} recorded={recorded} reforecast={reforecast} ramped={ramped?.plan}
            metrics={[
              { label: 'Revenue', field: 'revenue', format: 'money' },
              { label: 'Refunds', field: 'refunds', format: 'money' },
              { label: 'Income', field: 'income', format: 'money' },
              { label: 'New AOV', field: 'newAov', format: 'money' },
            ]}
          />
        </TabsContent>

        <TabsContent value="retention" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <ModelChart title="Paying customers" description="New plus renewing, in the month" format="count" data={series('payingCustomers')} />
            <ModelChart title="Renewing customers" format="count" data={series('recurringCustomers')} />
          </div>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Retention curve</CardTitle>
                  <CardDescription className="text-xs">
                    Share of a cohort paying N months after buying. Actual pools every cohort old enough to have
                    finished that month, so early months rest on few customers — hover for the count.
                  </CardDescription>
                </div>
                <div className="flex shrink-0 rounded-lg border border-input bg-background p-1">
                  {PLANS.map(p => (
                    <button
                      key={p}
                      onClick={() => setRetentionPlan(p)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        retentionPlan === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {PLAN_LABELS[p]}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={retention} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                  <XAxis dataKey="age" tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={age => `M${age}`} />
                  <YAxis domain={[0, 1]} tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={v => formatPercent(v, 0)} width={44} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    labelFormatter={age => `Month ${age} after purchase`}
                    formatter={(value, name, item) => [
                      name === 'Actual' ? `${formatPercent(value)} of ${item.payload.cohortSize}` : formatPercent(value),
                      name,
                    ]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconSize={10} />
                  <Line type="monotone" dataKey="model" name="Plan" stroke={SERIES_COLORS.plan} strokeWidth={2} strokeDasharray="6 4" dot={false} />
                  <Line type="monotone" dataKey="actual" name="Actual" stroke={SERIES_COLORS.actual} strokeWidth={2} dot={{ r: 4 }} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
              {usable?.cohorts_checked && retention.every(point => point.actual == null) && (
                <p className="text-xs text-muted-foreground text-center">
                  No {PLAN_LABELS[retentionPlan].toLowerCase()} cohort has finished its first month yet.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pnl" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-3">
            <ModelChart title="Income" height={220} data={series('income')} />
            <ModelChart title="Gross profit" height={220} data={series('grossProfit')} />
            <ModelChart title="Net operating income" height={220} data={series('netOperatingIncome')} />
          </div>
          <PnlStatement plan={plan} reforecast={reforecast} ramped={ramped?.plan} />
        </TabsContent>

        <TabsContent value="assumptions">
          {levered && (
            <div className="mb-4">
              <Notice>Levers are applied on top of these numbers. The charts show both; these fields show the numbers before the levers.</Notice>
            </div>
          )}
          <AssumptionsEditor
            a={assumptions}
            onEdit={onEdit}
            sheetPrices={SCIENCE_MODEL_DEFAULTS.prices}
            priceSources={baseline.sources}
            livePrices={livePrices}
          />
        </TabsContent>
      </Tabs>
      </>
      )}
    </div>
  )
}
